import { makeObservable, observable, action, runInAction } from 'mobx';
import petService from '../services/petService';

class PetStore {
  // Observable state
  categories = [];
  breeds = [];
  allBreeds = [];  // Store all breeds for later filtering
  myPets = [];
  selectedPet = null;
  mySuggestions = [];
  myBreedSuggestions = [];
  loading = false;
  error = null;

  // Premium & Calendar state
  premiumStatus = null;
  events = [];
  upcomingEvents = [];
  calendarEvents = [];
  calendarYear = new Date().getFullYear();
  calendarMonth = new Date().getMonth() + 1;
  premiumLoading = false;
  calendarLoading = false;

  constructor(rootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      categories: observable,
      breeds: observable,
      allBreeds: observable,
      myPets: observable,
      selectedPet: observable,
      mySuggestions: observable,
      myBreedSuggestions: observable,
      loading: observable,
      error: observable,
      // Premium & Calendar observables
      premiumStatus: observable,
      events: observable,
      upcomingEvents: observable,
      calendarEvents: observable,
      calendarYear: observable,
      calendarMonth: observable,
      premiumLoading: observable,
      calendarLoading: observable,
      // Actions
      fetchAnimalCategories: action,
      fetchBreeds: action,
      fetchAllBreeds: action,
      filterBreedsByCategory: action,
      fetchMyPets: action,
      fetchPetDetail: action,
      createPet: action,
      updatePet: action,
      deletePet: action,
      fetchMySuggestions: action,
      submitCategorySuggestion: action,
      fetchMyBreedSuggestions: action,
      submitBreedSuggestion: action,
      clearError: action,
      clearSelectedPet: action,
      reset: action,
      // Premium actions
      fetchPremiumStatus: action,
      startTrial: action,
      createSubscription: action,
      cancelSubscription: action,
      verifyCheckoutSession: action,
      // Calendar actions
      fetchEvents: action,
      createEvent: action,
      updateEvent: action,
      deleteEvent: action,
      completeEvent: action,
      fetchUpcomingEvents: action,
      fetchCalendarMonth: action,
      setCalendarMonth: action
    });
  }

  /**
   * Fetch all animal categories
   */
  async fetchAnimalCategories() {
    this.loading = true;
    this.error = null;

    const result = await petService.fetchAnimalCategories();

    runInAction(() => {
      if (result.success) {
        this.categories = result.data.results || result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Fetch breeds for a specific animal category
   * @param {number} categoryId - Animal category ID
   */
  async fetchBreeds(categoryId) {
    this.loading = true;
    this.error = null;

    const result = await petService.fetchBreeds(categoryId);

    runInAction(() => {
      if (result.success) {
        this.breeds = result.data.results || result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Fetch all breeds (for caching purposes)
   */
  async fetchAllBreeds() {
    const result = await petService.fetchBreeds();

    runInAction(() => {
      if (result.success) {
        // Handle both paginated and non-paginated responses
        const data = result.data.results || result.data;
        this.allBreeds = Array.isArray(data) ? data : [];
      }
    });

    return result.success;
  }

  /**
   * Filter already-loaded breeds by category
   * @param {number} categoryId - Animal category ID
   */
  filterBreedsByCategory(categoryId) {
    if (categoryId && this.allBreeds.length > 0) {
      const catId = parseInt(categoryId, 10);
      this.breeds = this.allBreeds.filter(
        breed => parseInt(breed.animal_category, 10) === catId
      );
    } else if (!categoryId) {
      this.breeds = [...this.allBreeds];
    }
  }

  /**
   * Fetch user's pets
   */
  async fetchMyPets() {
    this.loading = true;
    this.error = null;

    const result = await petService.fetchMyPets();

    runInAction(() => {
      if (result.success) {
        this.myPets = result.data.results || result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Fetch single pet detail
   * @param {number} petId - Pet ID
   */
  async fetchPetDetail(petId) {
    this.loading = true;
    this.error = null;

    const result = await petService.fetchPetDetail(petId);

    runInAction(() => {
      if (result.success) {
        this.selectedPet = result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Create a new pet
   * @param {Object} petData - Pet data
   */
  async createPet(petData) {
    this.loading = true;
    this.error = null;

    const result = await petService.createPet(petData);

    runInAction(() => {
      if (result.success) {
        // Add new pet to the beginning of the list
        this.myPets = [result.data, ...this.myPets];
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  /**
   * Update an existing pet
   * @param {number} petId - Pet ID
   * @param {Object} petData - Updated pet data
   */
  async updatePet(petId, petData) {
    this.loading = true;
    this.error = null;

    const result = await petService.updatePet(petId, petData);

    runInAction(() => {
      if (result.success) {
        // Update the pet in the list
        const index = this.myPets.findIndex(pet => pet.id === petId);
        if (index !== -1) {
          this.myPets[index] = result.data;
        }
        this.selectedPet = result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  /**
   * Delete a pet
   * @param {number} petId - Pet ID
   */
  async deletePet(petId) {
    this.loading = true;
    this.error = null;

    const result = await petService.deletePet(petId);

    runInAction(() => {
      if (result.success) {
        // Remove pet from the list
        this.myPets = this.myPets.filter(pet => pet.id !== petId);
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  /**
   * Clear error message
   */
  clearError() {
    this.error = null;
  }

  /**
   * Clear selected pet
   */
  clearSelectedPet() {
    this.selectedPet = null;
  }

  /**
   * Fetch user's category suggestions
   */
  async fetchMySuggestions() {
    this.loading = true;
    this.error = null;

    const result = await petService.fetchMySuggestions();

    runInAction(() => {
      if (result.success) {
        this.mySuggestions = result.data.results || result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Submit a new category suggestion
   * @param {Object} suggestionData - { suggested_name, description }
   */
  async submitCategorySuggestion(suggestionData) {
    this.loading = true;
    this.error = null;

    const result = await petService.submitCategorySuggestion(suggestionData);

    runInAction(() => {
      if (result.success) {
        // Add new suggestion to the list
        this.mySuggestions = [result.data, ...this.mySuggestions];
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  /**
   * Fetch user's breed suggestions
   */
  async fetchMyBreedSuggestions() {
    this.loading = true;
    this.error = null;

    const result = await petService.fetchMyBreedSuggestions();

    runInAction(() => {
      if (result.success) {
        this.myBreedSuggestions = result.data.results || result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result.success;
  }

  /**
   * Submit a new breed suggestion
   * @param {Object} suggestionData - { animal_category, suggested_name, description }
   */
  async submitBreedSuggestion(suggestionData) {
    this.loading = true;
    this.error = null;

    const result = await petService.submitBreedSuggestion(suggestionData);

    runInAction(() => {
      if (result.success) {
        // Add new suggestion to the list
        this.myBreedSuggestions = [result.data, ...this.myBreedSuggestions];
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  /**
   * Reset store (on logout)
   */
  reset() {
    this.myPets = [];
    this.selectedPet = null;
    this.mySuggestions = [];
    this.myBreedSuggestions = [];
    this.loading = false;
    this.error = null;
    this.premiumStatus = null;
    this.events = [];
    this.upcomingEvents = [];
    this.calendarEvents = [];
    // Keep categories and breeds as they are static data
  }

  // ============================================
  // PREMIUM SUBSCRIPTION
  // ============================================

  /**
   * Fetch premium subscription status
   */
  async fetchPremiumStatus() {
    this.premiumLoading = true;
    this.error = null;

    const result = await petService.getPremiumStatus();

    runInAction(() => {
      if (result.success) {
        this.premiumStatus = result.data;
      } else {
        this.error = result.error;
      }
      this.premiumLoading = false;
    });

    return result;
  }

  /**
   * Start free trial
   */
  async startTrial() {
    this.premiumLoading = true;
    this.error = null;

    const result = await petService.startTrial();

    runInAction(() => {
      if (result.success) {
        this.premiumStatus = {
          has_subscription: true,
          subscription: result.data.subscription,
          is_premium: true
        };
      } else {
        this.error = result.error;
      }
      this.premiumLoading = false;
    });

    return result;
  }

  /**
   * Create checkout session for subscription
   */
  async createSubscription(options = {}) {
    this.premiumLoading = true;
    this.error = null;

    const result = await petService.createSubscription(options);

    runInAction(() => {
      if (!result.success) {
        this.error = result.error;
      }
      this.premiumLoading = false;
    });

    return result;
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription() {
    this.premiumLoading = true;
    this.error = null;

    const result = await petService.cancelSubscription();

    runInAction(() => {
      if (result.success) {
        this.premiumStatus = {
          ...this.premiumStatus,
          subscription: result.data.subscription
        };
      } else {
        this.error = result.error;
      }
      this.premiumLoading = false;
    });

    return result;
  }

  /**
   * Verify checkout session after payment
   */
  async verifyCheckoutSession(sessionId) {
    this.premiumLoading = true;
    this.error = null;

    const result = await petService.verifyCheckoutSession(sessionId);

    runInAction(() => {
      if (result.success) {
        this.premiumStatus = {
          has_subscription: true,
          subscription: result.data.subscription,
          is_premium: true
        };
      } else {
        this.error = result.error;
      }
      this.premiumLoading = false;
    });

    return result;
  }

  // ============================================
  // PET CALENDAR
  // ============================================

  /**
   * Fetch all events for user's pets
   */
  async fetchEvents(params = {}) {
    this.calendarLoading = true;
    this.error = null;

    const result = await petService.fetchEvents(params);

    runInAction(() => {
      if (result.success) {
        this.events = result.data.results || result.data;
      } else {
        this.error = result.error;
      }
      this.calendarLoading = false;
    });

    return result;
  }

  /**
   * Create a new event
   */
  async createEvent(eventData) {
    this.calendarLoading = true;
    this.error = null;

    const result = await petService.createEvent(eventData);

    runInAction(() => {
      if (result.success) {
        this.events = [result.data, ...this.events];
        // Refresh upcoming events
        this.fetchUpcomingEvents();
        // Refresh calendar if viewing the event's month
        const eventDate = new Date(result.data.event_date);
        if (eventDate.getFullYear() === this.calendarYear &&
            eventDate.getMonth() + 1 === this.calendarMonth) {
          this.fetchCalendarMonth(this.calendarYear, this.calendarMonth);
        }
      } else {
        this.error = result.error;
      }
      this.calendarLoading = false;
    });

    return result;
  }

  /**
   * Update an event
   */
  async updateEvent(eventId, eventData) {
    this.calendarLoading = true;
    this.error = null;

    const result = await petService.updateEvent(eventId, eventData);

    runInAction(() => {
      if (result.success) {
        const index = this.events.findIndex(e => e.id === eventId);
        if (index !== -1) {
          this.events[index] = result.data;
        }
        // Refresh calendar
        this.fetchCalendarMonth(this.calendarYear, this.calendarMonth);
      } else {
        this.error = result.error;
      }
      this.calendarLoading = false;
    });

    return result;
  }

  /**
   * Delete an event
   */
  async deleteEvent(eventId) {
    this.calendarLoading = true;
    this.error = null;

    const result = await petService.deleteEvent(eventId);

    runInAction(() => {
      if (result.success) {
        this.events = this.events.filter(e => e.id !== eventId);
        this.calendarEvents = this.calendarEvents.filter(e => e.id !== eventId);
        this.upcomingEvents = this.upcomingEvents.filter(e => e.id !== eventId);
      } else {
        this.error = result.error;
      }
      this.calendarLoading = false;
    });

    return result;
  }

  /**
   * Mark event as completed
   */
  async completeEvent(eventId) {
    this.calendarLoading = true;
    this.error = null;

    const result = await petService.completeEvent(eventId);

    runInAction(() => {
      if (result.success) {
        const index = this.events.findIndex(e => e.id === eventId);
        if (index !== -1) {
          this.events[index] = result.data.event;
        }
        // If a new recurring event was created, add it
        if (result.data.next_event) {
          this.events = [...this.events, result.data.next_event];
        }
        // Refresh upcoming events
        this.fetchUpcomingEvents();
        // Refresh calendar
        this.fetchCalendarMonth(this.calendarYear, this.calendarMonth);
      } else {
        this.error = result.error;
      }
      this.calendarLoading = false;
    });

    return result;
  }

  /**
   * Fetch upcoming events
   */
  async fetchUpcomingEvents(days = 30) {
    this.calendarLoading = true;

    const result = await petService.fetchUpcomingEvents(days);

    runInAction(() => {
      if (result.success) {
        this.upcomingEvents = result.data.results || result.data;
      }
      this.calendarLoading = false;
    });

    return result;
  }

  /**
   * Fetch events for a specific month
   */
  async fetchCalendarMonth(year, month) {
    this.calendarLoading = true;

    const result = await petService.fetchCalendarMonth(year, month);

    runInAction(() => {
      if (result.success) {
        this.calendarEvents = result.data.events || [];
        this.calendarYear = year;
        this.calendarMonth = month;
      }
      this.calendarLoading = false;
    });

    return result;
  }

  /**
   * Set calendar month (for navigation)
   */
  setCalendarMonth(year, month) {
    this.calendarYear = year;
    this.calendarMonth = month;
    this.fetchCalendarMonth(year, month);
  }
}

export default PetStore;
